import {initContract} from '@ts-rest/core';
import {z} from 'zod';

const c = initContract();

// Bedrock Converse natively supports these formats; keeping the allowlist
// here means the upload validator and the LLM-ingestion layer can never
// drift apart.
export const ALLOWED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;

export const ALLOWED_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'text/html',
  'text/markdown',
  'text/plain'
] as const;

export const ALLOWED_MIME_TYPES = [...ALLOWED_IMAGE_MIME_TYPES, ...ALLOWED_DOCUMENT_MIME_TYPES] as const;

export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;
export const MAX_ATTACHMENTS_PER_MESSAGE = 3;

export const chatAttachmentResponseSchema = z.object({
  id: z.string().uuid(),
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  kind: z.enum(['image', 'document']),
  width: z.number().int().nullable().optional(),
  height: z.number().int().nullable().optional()
});

export type ChatAttachmentResponse = z.infer<typeof chatAttachmentResponseSchema>;

// Presigned-PUT upload flow. The previous multipart `POST /chat-attachments`
// route went through CloudFront → core-fn (BUFFERED Lambda Function URL,
// 6 MB request payload cap) and rejected any image > 6 MB or document > 6 MB
// at the FURL before invoking the Lambda.
//
//   1. POST /chat-attachments/presign → validates auth + MIME + size,
//      inserts the chat_attachments row, returns a short-lived S3 PUT URL
//      pinned to the declared Content-Type.
//   2. PUT <putUrl> → browser uploads bytes directly to S3.
//
// There is no /complete step: document → markdown extraction happens
// lazily inside the chat completion handler the first time an attachment
// is referenced, then the result is cached on the row's
// `extractedMarkdownKey` for subsequent turns. Keeps pdf2md off the 30 s
// CloudFront budget — chat-fn has 2 GB / 900 s to spend.
export const chatAttachmentsContract = c.router(
  {
    presign: {
      method: 'POST',
      path: '/presign',
      body: z.object({
        tenantId: z.string().uuid(),
        conversationId: z.string().uuid().optional(),
        filename: z.string().min(1),
        mimeType: z.string().min(1),
        sizeBytes: z.number().int().nonnegative()
      }),
      responses: {
        201: z.object({
          attachmentId: z.string().uuid(),
          putUrl: z.string().url()
        }),
        400: z.object({message: z.string()}),
        403: z.object({message: z.string()}),
        413: z.object({message: z.string()}),
        415: z.object({message: z.string()})
      },
      summary: 'Get a presigned S3 PUT URL for a chat attachment'
    },
    get: {
      method: 'GET',
      path: '/:id',
      pathParams: z.object({id: z.string().uuid()}),
      responses: {
        200: chatAttachmentResponseSchema,
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      },
      summary: 'Get a chat attachment metadata record'
    },
    delete: {
      method: 'DELETE',
      path: '/:id',
      pathParams: z.object({id: z.string().uuid()}),
      body: z.object({}).optional(),
      responses: {
        204: c.noBody(),
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()}),
        409: z.object({message: z.string()})
      },
      summary: 'Delete an unbound chat attachment'
    }
  },
  {
    pathPrefix: '/chat-attachments'
  }
);
